'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

// ---- SVG Icons ----
const PhoneIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.1 19.79 19.79 0 0 1 1.61 4.54 2 2 0 0 1 3.59 2.36h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.18 6.18l1.91-1.91a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const ArrowRight = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const CheckIcon = ({ size = 13, color = 'currentColor' }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2 6l3 3 5-5" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" aria-hidden="true">
    <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const DollarIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const ShieldIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const TrendUpIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const FileIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);

const ClockIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const MobileIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);

const CardIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);

const RefreshIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const BankIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="3" y1="22" x2="21" y2="22" />
    <line x1="6" y1="18" x2="6" y2="11" />
    <line x1="10" y1="18" x2="10" y2="11" />
    <line x1="14" y1="18" x2="14" y2="11" />
    <line x1="18" y1="18" x2="18" y2="11" />
    <polygon points="12 2 20 7 4 7" />
  </svg>
);

const AppleIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 814 1000" fill="currentColor" aria-hidden="true">
    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 70.1 0 128.4 2.6 198.3 99zM554.3 161.9c31.1-37.2 53.3-88.8 53.3-140.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.9-55.1 136.1 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.3-70.9z"/>
  </svg>
);

const GoogleIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 488 488" aria-hidden="true">
    <path fill="#4285F4" d="M488 244c0-16.3-1.4-32-4-47.2H244v89.3h136.9c-5.9 31.8-23.8 58.7-50.7 76.8v63.8h82c48-44.2 75.8-109.3 155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 70.1 0 128.4 2.6 198.3 99zM554.3 161.9c31.1-37.2 53.3-88.8 53.3-140.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.9-55.1 136.1 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.3-70.9z"/>
  </svg>
);

const SendIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const CollectIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SyncIcon = ({ size = 22 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
  </svg>
);

const PlusIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M7 3v8M3 7h8" stroke="#6B7280" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const MinusIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M3 7h8" stroke="#2F6BFF" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

// ---- Reveal Hook ----
function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('reveal-visible');
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

// ---- FAQ Item ----
function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item">
      <button
        className="w-full flex items-center justify-between py-5 text-left gap-4 hover:text-navy transition-colors"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="text-base font-semibold text-brand-fg pr-4">{question}</span>
        <span
          className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-transform duration-300"
          style={{ background: '#F5F7FB', transform: open ? 'rotate(45deg)' : 'rotate(0deg)' }}
          aria-hidden="true"
        >
          <PlusIcon />
        </span>
      </button>
      {open && (
        <p className="text-brand-body text-sm leading-relaxed pb-5 pr-10">{answer}</p>
      )}
    </div>
  );
}

// ---- Main Page ----
export default function HomePage() {
  // Reveal refs
  const heroRef = useRef<HTMLDivElement>(null);
  const r1 = useReveal(); const r2 = useReveal(); const r3 = useReveal();
  const r4 = useReveal(); const r5 = useReveal(); const r6 = useReveal();
  const r7 = useReveal(); const r8 = useReveal(); const r9 = useReveal();
  const r10 = useReveal(); const r11 = useReveal(); const r12 = useReveal();

  return (
    <div className="relative">
      {/* ===== HEADER ===== */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2.5 flex-shrink-0">
            <img
              src="https://plumbers.joinchargeact.com/assets/images/catext_logo-1773689960451.png"
              alt="ChargeAct Logo"
              className="h-10 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <a
              href="tel:+13237840656"
              className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-brand-body hover:text-navy transition-colors"
            >
              <PhoneIcon size={14} />
              +1 (323) 784-0656
            </a>
            <Link
              href="/apply"
              className="btn-primary-dark px-4 py-2 text-sm hidden sm:inline-flex items-center gap-1.5"
            >
              See How Much Your Firm Could Save
            </Link>
            <a
              href="tel:+13237840656"
              className="sm:hidden flex items-center justify-center w-9 h-9 rounded-lg bg-accent-pale text-accent"
              aria-label="Call ChargeAct"
            >
              <PhoneIcon size={16} />
            </a>
          </div>
        </div>
      </header>

      <main>
        {/* ===== SECTION 1: HERO ===== */}
        <section
          className="relative min-h-screen flex items-center pt-16 overflow-hidden noise-overlay"
          style={{ background: 'linear-gradient(135deg, #041f4d 0%, #0A3D91 50%, #2F6BFF 100%)' }}
          aria-label="Hero"
        >
          {/* Grid overlay */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
            aria-hidden="true"
          />
          <div
            className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(47,107,255,0.25) 0%, transparent 70%)' }}
            aria-hidden="true"
          />
          <div
            className="absolute bottom-1/4 left-1/3 w-64 h-64 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(47,107,255,0.15) 0%, transparent 70%)' }}
            aria-hidden="true"
          />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 w-full relative z-10">
            <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              {/* Left column */}
              <div className="lg:col-span-6 xl:col-span-5">
                <div
                  className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full"
                  style={{ background: 'rgba(47,107,255,0.2)', border: '1px solid rgba(47,107,255,0.4)' }}
                >
                  <span className="pulse-dot w-1.5 h-1.5 rounded-full bg-accent-light flex-shrink-0" />
                  <span className="text-xs font-semibold text-accent-light tracking-widest uppercase">
                    PAYMENT PROCESSING FOR CPA FIRMS
                  </span>
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-extrabold text-white leading-tight tracking-tight mb-6">
                  Are Hidden Processing Fees Eating Into Your{' '}
                  <span style={{ color: '#6B9FFF' }}>Clients&apos; Bottom Line?</span>
                </h1>

                <p className="text-lg sm:text-xl text-white/75 font-normal leading-relaxed mb-8 max-w-xl">
                  Get transparent rates, same-day approvals, and a dedicated account manager &mdash; built for accounting professionals.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                  <Link href="/apply" className="btn-primary px-6 py-3.5 text-base font-bold text-center">
                    See How Much Your Firm Could Save
                  </Link>
                  <a href="tel:+13237840656" className="btn-secondary px-6 py-3.5 text-base font-semibold text-center">
                    Talk to a Payment Specialist
                  </a>
                </div>

                <p className="text-sm text-white/60 font-medium">
                  No obligation &middot; Free analysis &middot; Same-day response
                </p>
              </div>

              {/* Right column - Hero card */}
              <div className="lg:col-span-6 xl:col-span-7 flex justify-center lg:justify-end">
                <div className="relative w-full max-w-md lg:max-w-lg xl:max-w-xl">
                  {/* Invoice card */}
                  <div className="glass-card rounded-2xl p-6 mb-4 shadow-2xl">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-xs font-semibold text-white/50 uppercase tracking-widest mb-1">Digital Invoice</p>
                        <p className="text-white font-bold text-lg">Example: Tax Preparation Service</p>
                        <p className="text-white/60 text-sm">Completed &middot; Documented &middot; Signed</p>
                      </div>
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ background: 'rgba(47,107,255,0.25)' }}
                      >
                        <FileIcon size={22} />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {[
                        { label: 'Invoice', value: '$2,500.00', color: 'text-white' },
                        { label: 'Processing Fee', value: '$55.75', crossed: '$76.25', sub: 'ChargeAct rate', color: 'text-white' },
                        { label: 'You Keep', value: '$2,444.25', color: 'text-green-400' },
                      ].map((item) => (
                        <div key={item.label} className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                          <p className="text-white/50 text-xs mb-1">{item.label}</p>
                          {(item as any).crossed && <p className="text-white/40 text-xs line-through decoration-red-400 mb-0.5">{(item as any).crossed}</p>}
                          <p className={`font-bold text-sm ${item.color}`}>{item.value}</p>
                          {item.sub && <p className="text-white/40 text-xs mt-0.5">{item.sub}</p>}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-white/50">vs. avg competitors: <span className="text-white/50">$76.25</span> <span className="text-white/50">· you keep $2,423.75</span></span>
                      <span className="text-xs font-bold text-green-400 bg-green-400/10 px-2 py-0.5 rounded-full">Save $20.50</span>
                    </div>
                  </div>

                  {/* Stat cards */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="glass-card rounded-xl p-4 float-card-1">
                      <div className="flex items-center gap-1.5 mb-2">
                        <span className="text-red-400"><DollarIcon size={16} /></span>
                        <span className="text-white/50 text-xs font-medium truncate">Annual Fee Bleed</span>
                      </div>
                      <p className="font-extrabold text-sm leading-tight text-red-400">$9,150+</p>
                      <p className="text-white/40 text-xs mt-1 leading-tight">on avg volume at leading processors</p>
                    </div>
                    <div className="glass-card rounded-xl p-4 float-card-2">
                      <div className="flex items-center gap-1.5 mb-2">
                        <span className="text-green-400"><ShieldIcon size={16} /></span>
                        <span className="text-white/50 text-xs font-medium truncate">Audit Ready</span>
                      </div>
                      <p className="font-extrabold text-sm leading-tight text-green-400">Full Records</p>
                      <span className="inline-block mt-1 text-xs font-bold text-green-400 bg-green-400/15 px-1.5 py-0.5 rounded-full">✓ Compliant</span>
                      <p className="text-white/40 text-xs mt-1 leading-tight">Easy exports for bookkeeping</p>
                    </div>
                    <div className="glass-card rounded-xl p-4 float-card-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <span className="text-accent-light"><TrendUpIcon size={16} /></span>
                        <span className="text-white/50 text-xs font-medium truncate">Rate Reduced</span>
                      </div>
                      <p className="font-extrabold text-sm leading-tight text-white">Saving $214/mo</p>
                      <p className="text-white/40 text-xs mt-1 leading-tight">vs. flat-rate on same volume</p>
                    </div>
                  </div>

                  {/* Approval badge */}
                  <div className="glass-card rounded-xl p-4 mt-3 flex items-center gap-3 float-card-2">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: 'rgba(16,185,129,0.2)' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm">Account Approved: As Early As Same Day</p>
                      <p className="text-white/50 text-xs">Dedicated rep assigned &middot; Ready to process</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===== SECTION 2: THE REAL COST ===== */}
        <section id="real-cost" className="py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div ref={r1} className="text-center max-w-3xl mx-auto mb-14 reveal-hidden">
              <p className="eyebrow mb-3">THE REAL COST</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-brand-fg tracking-tight leading-tight mb-5">
                Most firms are still processing client payments the old way.
              </h2>
              <p className="text-lg text-brand-body leading-relaxed">
                Here&apos;s how that&apos;s costing you &mdash; and your clients.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {/* Left card - negative */}
              <div ref={r2} className="comparison-card reveal-hidden stagger-1">
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: '#FEF2F2', color: '#EF4444' }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-brand-fg text-base">Traditional Processors / Manual Checks</h3>
                      <p className="text-brand-muted text-xs">Slow, expensive, outdated</p>
                    </div>
                  </div>
                  <div className="space-y-3 mb-5">
                    {[
                      { label: 'PROFESSIONALISM', text: 'Manual invoicing erodes client trust' },
                      { label: 'DOCUMENTATION TRAIL', text: 'Paper checks and spreadsheets' },
                      { label: 'DISPUTE SUPPORT', text: "No protection \u2014 you're on your own" },
                      { label: 'DEDICATED REP', text: '1-800 numbers and hold queues' },
                      { label: 'PRICING TRANSPARENCY', text: 'Tiered pricing with hidden markups' },
                    ].map((item) => (
                      <div key={item.label} className="flex flex-col gap-0.5">
                        <span className="text-xs font-semibold text-brand-muted uppercase tracking-wider">{item.label}</span>
                        <div className="flex items-start gap-1.5">
                          <span className="flex-shrink-0 mt-0.5 text-red-400"><XIcon /></span>
                          <span className="text-sm text-brand-body">{item.text}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-xl px-4 py-2.5 text-center" style={{ background: '#FEF2F2' }}>
                    <span className="text-sm font-bold text-red-500">Not built for accounting firms</span>
                  </div>
                </div>
              </div>

              {/* Right card - positive */}
              <div ref={r3} className="comparison-card featured reveal-hidden stagger-2">
                <div className="bg-accent text-white text-xs font-bold tracking-widest uppercase text-center py-2 rounded-t-2xl">
                  ✓ RECOMMENDED FOR CPAs
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: '#2F6BFF', color: '#fff' }}
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M3 9L7.5 4.5L12 9L7.5 13.5L3 9Z" fill="white" fillOpacity="0.65" />
                        <path d="M7.5 9L12 4.5L16.5 9L12 13.5L7.5 9Z" fill="white" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-brand-fg text-base">ChargeAct</h3>
                      <p className="text-brand-muted text-xs">Built for professional service firms</p>
                    </div>
                  </div>
                  <div className="space-y-3 mb-5">
                    {[
                      { label: 'PROFESSIONALISM', text: 'Branded digital invoicing your clients expect' },
                      { label: 'DOCUMENTATION TRAIL', text: 'Full payment history for audit-ready records' },
                      { label: 'DISPUTE SUPPORT', text: 'Dedicated rep + chargeback assistance' },
                      { label: 'DEDICATED REP', text: 'Named contact, real phone number' },
                      { label: 'PRICING TRANSPARENCY', text: 'Competitive interchange-plus rates \u2014 no guessing' },
                    ].map((item) => (
                      <div key={item.label} className="flex flex-col gap-0.5">
                        <span className="text-xs font-semibold text-brand-muted uppercase tracking-wider">{item.label}</span>
                        <div className="flex items-start gap-1.5">
                          <span className="flex-shrink-0 mt-0.5 text-green-500"><CheckIcon /></span>
                          <span className="text-sm text-brand-fg font-medium">{item.text}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-xl px-4 py-2.5 text-center" style={{ background: '#EBF1FF' }}>
                    <span className="text-sm font-bold text-accent">Built to protect your firm&apos;s revenue</span>
                  </div>
                </div>
              </div>
            </div>

            <div ref={r4} className="mt-10 text-center reveal-hidden">
              <p className="text-brand-body text-base max-w-2xl mx-auto mb-8">
                <strong className="text-brand-fg">
                  When clients owe you for tax prep, advisory work, or monthly bookkeeping &mdash; don&apos;t risk slow payments.
                </strong>{' '}
                Give them flexible options to pay on time, every time.
              </p>
              <Link href="/apply" className="btn-primary-dark px-8 py-3.5 text-base font-bold inline-flex items-center gap-2">
                See How Much Your Firm Could Save <ArrowRight />
              </Link>
            </div>
          </div>
        </section>

        {/* ===== SECTION 3: FEE MATH / CALCULATOR ===== */}
        <section id="fee-math" className="py-20 lg:py-28" style={{ background: '#F9FAFB' }}>
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div ref={r5} className="text-center max-w-2xl mx-auto mb-12 reveal-hidden">
              <p className="eyebrow mb-3">THE FEE MATH</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-fg tracking-tight leading-tight mb-4">
                The fee math adds up fast on every client invoice.
              </h2>
              <p className="text-brand-body text-base">
                Here&apos;s an example of what a difference in processing could mean for your firm with ChargeAct pricing.
              </p>
            </div>

            <div ref={r6} className="rounded-2xl overflow-hidden shadow-card reveal-hidden" style={{ border: '1.5px solid #E5E7EB' }}>
              {/* Table header */}
              <div className="fee-table-header grid grid-cols-5 gap-0">
                {['SERVICE SCENARIO', 'PROCESSING VOLUME', 'AVERAGE FEE', 'CHARGEACT', 'YOU SAVE'].map((h, i) => (
                  <div key={h} className={`px-5 py-4 text-xs font-bold uppercase tracking-widest text-white/90 ${i > 0 ? 'text-center' : ''}`}>
                    {h}
                  </div>
                ))}
              </div>
              {/* Rows */}
              {[
                { scenario: '$500 monthly bookkeeping retainer', volume: '$500', avg: '$15.25', ca: '$11.15', save: '$4.10', bg: 'bg-white' },
                { scenario: '$2,500 tax preparation', volume: '$2,500', avg: '$76.25', ca: '$55.75', save: '$20.50', bg: 'bg-brand-secondary' },
                { scenario: '$5,000 quarterly advisory engagement', volume: '$5,000', avg: '$152.50', ca: '$109.25', save: '$43.25', bg: 'bg-white' },
              ].map((row) => (
                <div key={row.scenario} className={`grid grid-cols-5 gap-0 ${row.bg}`}>
                  <div className="px-5 py-4 text-sm font-semibold text-brand-fg">{row.scenario}</div>
                  <div className="px-5 py-4 text-sm text-center text-brand-fg font-semibold">{row.volume}</div>
                  <div className="px-5 py-4 text-sm text-center text-red-500 font-semibold line-through decoration-red-300">{row.avg}</div>
                  <div className="px-5 py-4 text-sm text-center text-accent font-bold">{row.ca}</div>
                  <div className="px-5 py-4 text-sm text-center font-bold text-green-600 text-base">{row.save}</div>
                </div>
              ))}
              {/* Annual row */}
              <div className="grid grid-cols-5 gap-0 bg-accent/5 border-t-2 border-accent/30">
                <div className="px-5 py-4 text-sm font-bold text-brand-fg">
                  200 client invoices/yr avg $1,500
                  <span className="block text-xs font-normal text-brand-muted mt-0.5">Annual estimate</span>
                </div>
                <div className="px-5 py-4 text-sm text-center text-brand-fg font-bold">$300,000/yr</div>
                <div className="px-5 py-4 text-sm text-center text-red-500 font-semibold line-through decoration-red-300">$9,150/yr</div>
                <div className="px-5 py-4 text-sm text-center text-accent font-bold">$6,585/yr</div>
                <div className="px-5 py-4 text-sm text-center font-bold text-green-600 text-base">$2,565/yr</div>
              </div>
            </div>

            <p className="text-center text-xs text-brand-muted mt-4 leading-relaxed">
              Rates are subject to merchant history and prices vary between businesses. Actual savings vary. For illustrative purposes only.
            </p>

            <div className="text-center mt-8">
              <Link href="/apply" className="btn-primary-dark px-8 py-3.5 text-base font-bold inline-flex items-center gap-2">
                See How Much Your Firm Could Save <ArrowRight />
              </Link>
            </div>
          </div>
        </section>

        {/* ===== SECTION 4: INVOICING ===== */}
        <section id="invoicing" className="py-20 lg:py-28" style={{ background: '#F9FAFB', borderTop: '1px solid #E5E7EB' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div ref={r7} className="text-center max-w-2xl mx-auto mb-14 reveal-hidden">
              <p className="eyebrow mb-3">INVOICING</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-fg tracking-tight leading-tight mb-4">
                Send an invoice. Get paid the same day.
              </h2>
              <p className="text-brand-body text-base">
                Create and send professional invoices digitally from anywhere. Clients pay instantly by card, ACH, or bank transfer &mdash; no chasing checks or waiting for mail.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto mb-10">
              {[
                {
                  step: '01',
                  icon: <FileIcon size={20} />,
                  title: 'Create',
                  desc: 'Build a branded invoice in seconds from your dashboard or mobile.',
                },
                {
                  step: '02',
                  icon: <SendIcon size={20} />,
                  title: 'Send',
                  desc: 'Email or text a payment link directly to your client.',
                },
                {
                  step: '03',
                  icon: <CollectIcon size={20} />,
                  title: 'Collect',
                  desc: 'Client pays instantly. Funds hit your account next business day.',
                },
              ].map((item, i) => (
                <div
                  key={item.step}
                  className="relative rounded-2xl p-6"
                  ref={i === 0 ? r8 : undefined}
                  style={{ background: '#ffffff', border: '1.5px solid #E5E7EB' }}
                >
                  <div className="text-5xl font-extrabold leading-none mb-4" style={{ color: '#E5E7EB', fontVariantNumeric: 'tabular-nums' }}>
                    {item.step}
                  </div>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3 text-accent" style={{ background: '#EBF1FF' }}>
                    {item.icon}
                  </div>
                  <h3 className="font-bold text-brand-fg text-base mb-2">{item.title}</h3>
                  <p className="text-brand-body text-sm leading-relaxed">{item.desc}</p>
                  {i < 2 && (
                    <div
                      className="hidden sm:block absolute top-10 -right-3 w-6 h-0.5 z-10"
                      style={{ background: 'linear-gradient(90deg, #2F6BFF 0%, #E5E7EB 100%)' }}
                      aria-hidden="true"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="text-center">
              <a href="tel:+13237840656" className="btn-primary-dark px-8 py-3.5 text-base font-bold inline-flex items-center gap-2">
                <PhoneIcon size={16} /> Talk to a Payment Specialist
              </a>
            </div>
          </div>
        </section>

        {/* ===== SECTION 5: WHY CPAs CHOOSE CHARGEACT ===== */}
        <section id="solutions" className="py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div ref={r9} className="text-center max-w-2xl mx-auto mb-14 reveal-hidden">
              <p className="eyebrow mb-3">THE CHARGEACT ADVANTAGE</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-fg tracking-tight leading-tight mb-4">
                Streamline your firm, save money, and get paid faster.
              </h2>
              <p className="text-brand-body text-base">
                Five ways ChargeAct helps CPA firms run leaner operations, reduce costs, and give clients the payment flexibility they expect.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                {
                  icon: <MobileIcon />,
                  title: 'More Ways to Get Paid',
                  desc: 'Accept cards, ACH, digital wallets, and bank transfers \u2014 giving clients the flexibility to pay however they prefer. More payment options means fewer outstanding invoices and faster collections.',
                },
                {
                  icon: <FileIcon />,
                  title: 'Streamlined Invoicing & Billing',
                  desc: 'Create and send professional invoices from anywhere in seconds. Clients approve and pay in one seamless flow \u2014 eliminating back-and-forth, cutting admin time, and getting money in your account faster.',
                },
                {
                  icon: <ClockIcon />,
                  title: 'Save Time Every Month',
                  desc: 'Automated reminders, recurring billing for retainer clients, and one-click payments eliminate the manual tasks that eat into your day. Spend less time chasing payments and more time on billable work.',
                },
                {
                  icon: <DollarIcon />,
                  title: 'Lower Processing Costs',
                  desc: 'Competitive interchange-plus rates designed for professional service firms mean you keep more of every dollar you earn. No hidden fees, no tiered pricing games.',
                },
                {
                  icon: <SyncIcon />,
                  title: 'QuickBooks & Accounting Integration',
                  desc: 'All payment data syncs seamlessly with QuickBooks, Xero, and other accounting platforms \u2014 keeping your books accurate without manual reconciliation.',
                },
              ].map((item, i) => (
                <div key={item.title} className="solution-card p-6">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 text-accent" style={{ background: '#EBF1FF' }}>
                    {item.icon}
                  </div>
                  <h3 className="font-bold text-brand-fg text-base mb-2">{item.title}</h3>
                  <p className="text-brand-body text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link href="/apply" className="btn-primary-dark px-8 py-3.5 text-base font-bold inline-flex items-center gap-2">
                See How Much Your Firm Could Save <ArrowRight />
              </Link>
            </div>
          </div>
        </section>

        {/* ===== SECTION 6: PAYMENT METHODS ===== */}
        <section id="payment-methods" className="py-20 lg:py-28" style={{ background: '#F9FAFB', borderTop: '1px solid #E5E7EB' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div ref={r10} className="text-center max-w-2xl mx-auto mb-14 reveal-hidden">
              <p className="eyebrow mb-3">PAYMENT METHODS</p>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-brand-fg tracking-tight leading-tight mb-4">
                Every card. Every payment type.
              </h2>
              <p className="text-brand-body text-base">
                Accept anything your clients want to pay with &mdash; in person, online, or on the go.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
              {[
                { icon: <CardIcon size={28} />, title: 'Credit & Debit Cards', desc: 'All major credit & debit cards accepted' },
                { icon: <AppleIcon size={28} />, title: 'Apple Pay', desc: 'Tap-to-pay on iPhone & Mac' },
                { icon: <GoogleIcon size={28} />, title: 'Google Pay', desc: 'Android & contactless payments' },
                { icon: <BankIcon size={28} />, title: 'ACH / Bank Transfer', desc: 'Direct bank-to-bank payments' },
                { icon: <FileIcon size={28} />, title: 'Invoicing', desc: 'Send & collect digital invoices' },
                { icon: <RefreshIcon size={28} />, title: 'Recurring Billing', desc: 'Automated retainer & subscription billing' },
              ].map((item, i) => (
                <div
                  key={item.title}
                  className="integration-badge flex flex-col items-center gap-3 px-4 py-6 text-center"
                >
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-accent" style={{ background: '#EBF1FF' }}>
                    {item.icon}
                  </div>
                  <div>
                    <p className="font-bold text-brand-fg text-sm">{item.title}</p>
                    <p className="text-brand-muted text-xs mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center">
              <Link href="/apply" className="btn-primary-dark px-8 py-3.5 text-base font-bold inline-flex items-center gap-2">
                See How Much Your Firm Could Save <ArrowRight />
              </Link>
            </div>
          </div>
        </section>

        {/* ===== SECTION 7: BOTTOM CTA ===== */}
        <section
          id="final-cta"
          className="py-20 lg:py-28 relative overflow-hidden noise-overlay"
          style={{ background: 'linear-gradient(135deg, #041f4d 0%, #0A3D91 55%, #2F6BFF 100%)' }}
          aria-label="Final call to action"
        >
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
            aria-hidden="true"
          />
          <div
            className="absolute top-0 right-1/4 w-96 h-96 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(47,107,255,0.2) 0%, transparent 70%)' }}
            aria-hidden="true"
          />

          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
            <div
              className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full"
              style={{ background: 'rgba(47,107,255,0.2)', border: '1px solid rgba(47,107,255,0.4)' }}
            >
              <span className="text-xs font-semibold text-accent-light tracking-widest uppercase">FOR CPA FIRMS</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight mb-5">
              Find out what you&apos;re actually paying.
            </h2>
            <p className="text-lg text-white/75 leading-relaxed mb-10 max-w-2xl mx-auto">
              A dedicated manager will review your current rates and show you exactly where your firm is losing money &mdash; no obligation.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
              <Link href="/apply" className="btn-primary px-8 py-4 text-base font-bold text-center">
                See How Much Your Firm Could Save
              </Link>
              <a href="tel:+13237840656" className="btn-secondary px-8 py-4 text-base font-semibold text-center">
                Talk to a Payment Specialist
              </a>
            </div>

            <div className="flex flex-col items-center gap-3">
              <a
                href="tel:+13237840656"
                className="flex items-center gap-2 text-white font-bold text-xl hover:text-accent-light transition-colors"
              >
                <PhoneIcon size={20} />
                Call +1 (323) 784-0656
              </a>
              <div className="flex flex-wrap justify-center gap-6 text-xs text-white/60 font-medium">
                {['No obligation', 'Free analysis', 'Same-day response', 'No long-term contract'].map((item) => (
                  <span key={item} className="flex items-center gap-1.5">
                    <CheckIcon size={12} color="#5B8BFF" />
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-brand-border py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img
                src="https://plumbers.joinchargeact.com/assets/images/catext_logo-1773689960451.png"
                alt="ChargeAct Logo"
                className="h-8 w-auto"
              />
            </div>
            <div className="text-xs text-brand-muted text-center">
&copy; 2026 ChargeAct. All Rights Reserved.
            </div>
            <div className="flex items-center gap-4 text-xs text-brand-muted">
              <Link href="/terms" className="hover:text-navy transition-colors">Terms &amp; Conditions</Link>
              <Link href="/privacy" className="hover:text-navy transition-colors">Privacy Policy</Link>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-brand-border">
            <p className="text-[10px] leading-relaxed text-brand-muted">
              <span className="font-semibold">IMPORTANT NOTICE — ILLUSTRATIVE DATA ONLY:</span> The fee comparison figures, savings estimates, processing rate examples, annual cost projections, and all related financial data presented on this page, including but not limited to the fee savings comparison table, the floating savings indicator, and any referenced dollar amounts, are provided solely for illustrative and informational purposes. Such figures constitute hypothetical examples based on generalized assumptions and do not represent actual, guaranteed, or projected results for any specific merchant. Actual processing rates, fees, and savings will vary materially based on individual merchant history, transaction volume, card mix, chargeback history, business type, applicable interchange categories, and other underwriting factors determined at the sole discretion of the acquiring bank and payment network. Nothing contained herein shall constitute a representation, warranty, or guarantee of any specific rate, fee, or savings outcome. Past performance or illustrative examples are not indicative of future results. All rates and terms are subject to change without notice and are contingent upon merchant approval. ChargeAct makes no representations or warranties, express or implied, regarding the accuracy, completeness, or applicability of the illustrative data to any particular merchant&apos;s circumstances.
            </p>
          </div>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      <div className="sticky-cta bg-navy px-4 py-3 flex-col gap-2 shadow-2xl">
        <Link href="/apply" className="btn-primary w-full py-3 text-sm text-center font-bold">
          See How Much Your Firm Could Save
        </Link>
        <a href="tel:+13237840656" className="text-center text-xs text-white/70 font-medium">
          Or call +1 (323) 784-0656 &mdash; talk to a specialist now
        </a>
      </div>
    </div>
  );
}
